"use client"

import { useState, useEffect } from "react"
import { Search, X } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { httpJson } from "@/lib/http"
import { CRM_API_BASE } from "@/lib/config"

interface Client {
  id: number
  company_name?: string
  first_name?: string
  last_name?: string
  email?: string
}

const statusDisplayLabels: Record<string, string> = {
  paused: "Paused",
  complete: "Completed",
  cancelled: "Cancelled",
  created: "Created",
  in_progress: "In Progress",
}

interface OrderFiltersProps {
  variant?: "default" | "compact"
  disabled?: boolean
  onSearchChange: (value: string) => void
  onStatusChange: (value: string) => void
  onClientChange: (clientId: string) => void
  onClearAll?: () => void
  searchValue?: string
  statusValue?: string
  clientValue?: string
  onClientsCountChange?: (count: number) => void
}

export function OrderFilters({
  variant = "default",
  disabled = false,
  onSearchChange,
  onStatusChange,
  onClientChange,
  onClearAll,
  searchValue = "",
  statusValue = "all",
  clientValue = "all",
  onClientsCountChange,
}: OrderFiltersProps) {
  const [searchInputValue, setSearchInputValue] = useState(searchValue)
  const [clients, setClients] = useState<Client[]>([])
  const [initialClients, setInitialClients] = useState<Client[]>([])
  const [clientSearchTerm, setClientSearchTerm] = useState("")
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [loadingClients, setLoadingClients] = useState(false)
  const [clientAbortController, setClientAbortController] = useState<AbortController | null>(null)
  const [selectedClientId, setSelectedClientId] = useState<string>(clientValue === "all" ? "" : clientValue)

  const isCompact = variant === "compact"

  const inputClass = isCompact
    ? "pl-7 border-gray-300 bg-white shadow-sm focus:border-primary focus:ring-1 focus:ring-primary transition-colors h-7 text-xs"
    : "pl-8 pr-8 h-9 border border-gray-400 bg-background focus:border-primary"

  const selectTriggerClass = isCompact
    ? "w-32 border-gray-300 bg-white shadow-sm focus:border-primary h-7 text-xs"
    : "w-40 h-9 border border-gray-400 bg-background focus:border-primary"

  const clientInputClass = isCompact
    ? "h-7 text-xs border-gray-300 bg-white shadow-sm focus:border-primary focus:ring-1 focus:ring-primary pr-8"
    : "h-9 border border-gray-400 bg-background focus:border-primary pr-8"

  const fetchClients = async (searchTerm = "", abortController?: AbortController) => {
    try {
      setLoadingClients(true)

      const url = searchTerm
        ? `${CRM_API_BASE}/client/?q=${encodeURIComponent(searchTerm)}&limit=20`
        : `${CRM_API_BASE}/client/?limit=20`

      const response = await httpJson<{ results?: Client[]; count?: number }>(url, {
        signal: abortController?.signal,
      })

      // Notify parent component of count change (only on initial load, not on search)
      if (!searchTerm && onClientsCountChange && response.count !== undefined) {
        onClientsCountChange(response.count)
      }

      if (response.results) {
        if (searchTerm) {
          setClients(response.results)
        } else {
          setInitialClients(response.results)
          setClients(response.results)
        }
      } else {
        if (!searchTerm) {
          setInitialClients([])
        }
        setClients([])
      }
    } catch (error) {
      if (error instanceof Error && (error.message.includes("Request was cancelled") || error.name === "AbortError")) {
        return
      }
      console.error("Error fetching clients:", error)
      setClients([])
    } finally {
      setLoadingClients(false)
      if (abortController) {
        setClientAbortController(null)
      }
    }
  }

  useEffect(() => {
    fetchClients()
  }, [])

  useEffect(() => {
    if (clientAbortController) {
      clientAbortController.abort()
    }

    if (clientSearchTerm.trim().length < 2) {
      setClients([...initialClients])
      return
    }

    const newAbortController = new AbortController()
    setClientAbortController(newAbortController)

    const timeoutId = setTimeout(() => {
      fetchClients(clientSearchTerm, newAbortController)
    }, 300)

    return () => {
      clearTimeout(timeoutId)
      if (newAbortController) {
        newAbortController.abort()
      }
    }
  }, [clientSearchTerm])

  useEffect(() => {
    if (searchInputValue === searchValue) return
    
    const timeoutId = setTimeout(() => {
      onSearchChange(searchInputValue)
    }, 600)

    return () => clearTimeout(timeoutId)
  }, [searchInputValue])

  useEffect(() => {
    // Sincronizar searchInputValue cuando searchValue cambia desde el padre (ej: clear filters)
    if (searchValue !== searchInputValue) {
      setSearchInputValue(searchValue)
    }
  }, [searchValue])

  const getClientDisplayName = (client: Client): string => {
    if (client.company_name) {
      return client.company_name
    }
    if (client.first_name || client.last_name) {
      return `${client.first_name || ""} ${client.last_name || ""}`.trim()
    }
    return "Unknown Client"
  }

  const clearSearch = () => {
    setSearchInputValue("")
    onSearchChange("")
  }

  const handleClientSelect = (clientId: string) => {
    setSelectedClientId(clientId)
    setClientSearchTerm("")
    setShowClientDropdown(false)
    onClientChange(clientId || "all")
  }

  const clearClientFilter = () => {
    setClientSearchTerm("")
    setSelectedClientId("")
    onClientChange("all")
    setShowClientDropdown(false)
  }

  return (
    <div className={`flex items-center ${isCompact ? "space-x-3" : "space-x-2"}`}>
      <div className={`relative ${isCompact ? "flex-1 max-w-sm" : "w-full max-w-sm"}`}>
        <Search className={`absolute left-2 ${isCompact ? "top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground/60" : "top-2.5 h-4 w-4"} ${disabled ? "text-muted-foreground opacity-50" : "text-muted-foreground"}`} />
        <Input
          placeholder="Search by Order ID..."
          className={`${inputClass} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          value={searchInputValue}
          onChange={(e) => setSearchInputValue(e.target.value)}
          disabled={disabled}
        />
        {searchInputValue && (
          <Button
            variant="ghost"
            size="sm"
            className={`absolute ${isCompact ? "right-1 top-1/2 -translate-y-1/2 h-5 w-5" : "right-1 top-1 h-6 w-6"} p-0 hover:bg-primary hover:text-primary-foreground`}
            onClick={clearSearch}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      <Select value={statusValue} onValueChange={onStatusChange} disabled={disabled}>
        <SelectTrigger className={selectTriggerClass}>
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent className={isCompact ? "border-slate-200/60" : ""}>
          <SelectItem value="all">All Statuses</SelectItem>
          <SelectItem value="created">{statusDisplayLabels.created}</SelectItem>
          <SelectItem value="in_progress">{statusDisplayLabels.in_progress}</SelectItem>
          <SelectItem value="paused">{statusDisplayLabels.paused}</SelectItem>
          <SelectItem value="cancelled">{statusDisplayLabels.cancelled}</SelectItem>
          <SelectItem value="complete">{statusDisplayLabels.complete}</SelectItem>
        </SelectContent>
      </Select>

      <div className={`relative ${isCompact ? "w-52" : "w-56"}`}>
        <Input
          placeholder={loadingClients ? "Loading clients..." : ""}
          value={clientSearchTerm || (selectedClientId && !showClientDropdown ? (() => {
            const selectedClient = clients.find(c => c.id.toString() === selectedClientId) || initialClients.find(c => c.id.toString() === selectedClientId)
            return selectedClient ? getClientDisplayName(selectedClient) : ""
          })() : (!clientSearchTerm && !selectedClientId && !showClientDropdown ? "All Clients" : ""))}
          onChange={(e) => {
            const searchValue = e.target.value
            setClientSearchTerm(searchValue)
            setShowClientDropdown(true)
            if (selectedClientId) {
              setSelectedClientId("")
              onClientChange("all")
            }
          }}
          onFocus={() => {
            if (!showClientDropdown && initialClients.length === 0) {
              fetchClients()
            }
            setShowClientDropdown(true)
            if (!clientSearchTerm && !selectedClientId) {
              setClientSearchTerm("")
            }
          }}
          onBlur={() => {
            setTimeout(() => {
              setShowClientDropdown(false)
              if (selectedClientId && !clientSearchTerm) {
                setClientSearchTerm("")
              }
            }, 200)
          }}
          className={`${clientInputClass} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          disabled={loadingClients || disabled}
        />
        <button
          type="button"
          onClick={() => {
            if (!showClientDropdown) {
              setShowClientDropdown(true)
              setClientSearchTerm("")
              if (initialClients.length === 0) {
                fetchClients()
              }
            } else {
              setShowClientDropdown(false)
            }
          }}
          className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-primary-foreground p-1 rounded hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed z-10 ${isCompact ? "right-1" : "right-2"}`}
          disabled={loadingClients || disabled}
        >
          <svg
            className={`${isCompact ? "w-3 h-3" : "w-4 h-4"} transition-transform duration-200 ${showClientDropdown ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {(clientSearchTerm || selectedClientId) && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              clearClientFilter()
            }}
            className={`absolute ${isCompact ? "right-7" : "right-10"} top-1/2 -translate-y-1/2 text-gray-600 hover:text-primary-foreground hover:bg-primary rounded p-1 z-20`}
          >
            <X className="h-3 w-3" />
          </button>
        )}
        {showClientDropdown && (
          <div
            className="absolute z-[9999] mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto scrollbar-minimal"
            style={{ left: "0", width: "100%" }}
          >
            {clientSearchTerm.trim().length > 0 && clientSearchTerm.trim().length < 2 ? (
              <div className={`p-3 ${isCompact ? "text-xs" : "text-sm"} text-gray-600`}>Type at least 2 characters to search...</div>
            ) : loadingClients ? (
              <div className={`p-3 ${isCompact ? "text-xs" : "text-sm"} text-gray-600`}>Searching clients...</div>
            ) : clients.length === 0 ? (
              <div className={`p-3 ${isCompact ? "text-xs" : "text-sm"} text-gray-600`}>
                {clientSearchTerm.trim()
                  ? `No clients found for "${clientSearchTerm}"`
                  : "No clients available"}
              </div>
            ) : (
              <div>
                <div
                  className={`px-4 py-3 hover:bg-blue-50 cursor-pointer ${isCompact ? "text-xs" : "text-sm"} text-gray-900 border-b border-gray-100`}
                  onClick={() => handleClientSelect("")}
                >
                  All Clients
                </div>
                {clients.map((client) => (
                  <div
                    key={client.id}
                    className={`px-4 py-3 hover:bg-blue-50 cursor-pointer ${isCompact ? "text-xs" : "text-sm"} text-gray-900 border-b border-gray-100 last:border-b-0`}
                    onClick={() => handleClientSelect(client.id.toString())}
                  >
                    {getClientDisplayName(client)}
                  </div>
                ))}
                {(clientSearchTerm.trim() === "" ? initialClients.length === 20 : clients.length === 20) && (
                  <div className="p-2 text-[11px] text-muted-foreground border-t bg-gray-50">
                    💡 Type a client name to search for more results
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Clear All Filters button - only shows when filters are active */}
      {onClearAll && (searchValue || statusValue !== "all" || clientValue !== "all") && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          disabled={disabled}
          className={`${isCompact ? "h-7 text-xs px-2" : "h-9"} text-muted-foreground hover:bg-primary hover:text-primary-foreground`}
        >
          Clear filters
        </Button>
      )}
    </div>
  )
}

